import {
  createAsyncThunk,
  createSlice,
  AnyAction,
  Action,
} from "@reduxjs/toolkit";
import CreateDiaryEntry from "./interfaces/CreateDiary";
import Diary from "./interfaces/IDiary";
import IDeleteDiaryEntry from "./interfaces/IDeleteDiaryEntry";
import IUpdateDiaryEntry from "./interfaces/IUpdateDiaryEntry";

interface DiaryState {
  entries: Diary[];
  isDiaryError: boolean;
  isDiarySuccess: boolean;
  isDiaryFetching: boolean;
  diaryError: string[];
  diarySuccess?: string[];
}

const initialState: DiaryState = {
  entries: [],
  isDiaryError: false,
  isDiarySuccess: false,
  isDiaryFetching: false,
  diaryError: [],
  diarySuccess: [],
};

export const getAllMyDiaryEntires = createAsyncThunk(
  "diary/me",
  async (_, thunkAPI) => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/diary/me`,
        {
          headers: { "Content-Type": "application/json" },
          credentials: "include",
        }
      );
      if (response.status === 200) {
        const data = await response.json();
        console.log(data);
        return data;
      }
      const { statusCode, message } = await response.json();
      return thunkAPI.rejectWithValue({ statusCode, message });
    } catch (error) {
      return thunkAPI.rejectWithValue({
        statusCode: 500,
        message: "Internal Server Error.",
      });
    }
  }
);

export const createDiaryEntry = createAsyncThunk(
  "diary/new",
  async (
    { entry, entry_date, entry_title, entry_time }: CreateDiaryEntry,
    thunkAPI
  ) => {
    console.log(`${entry_time} is the time in slice`);
    if (entry_title === "") {
      return thunkAPI.rejectWithValue({
        statusCode: 400,
        message: "Title cannot be blank",
      });
    }
    if (entry === "\n") {
      return thunkAPI.rejectWithValue({
        statusCode: 400,
        message: "Post cannot be blank",
      });
    }
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/diary/new`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({
            entry,
            entry_date,
            entry_title,
            entry_time,
          }),
        }
      );
      console.log(response);
      if (response.status === 201) {
        const data = await response.json();
        return data;
      }
      const { statusCode, message } = await response.json();
      return thunkAPI.rejectWithValue({ statusCode, message });
    } catch (error) {
      return thunkAPI.rejectWithValue({
        statusCode: 500,
        message: "Internal Server Error.",
      });
    }
  }
);

export const updateDiaryEntry = createAsyncThunk(
  "diary/update",
  async (
    { entry_id, entry, entry_title, entry_date }: IUpdateDiaryEntry,
    thunkAPI
  ) => {
    if (entry_title === "") {
      return thunkAPI.rejectWithValue({
        statusCode: 400,
        message: "Title cannot be blank",
      });
    }
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/diary/${entry_id}`,
        {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({
            entry,
            entry_title,
            entry_date,
          }),
        }
      );

      if (response.status === 200) {
        const data = await response.json();

        return data;
      }
      const { statusCode, message } = await response.json();

      return thunkAPI.rejectWithValue({ statusCode, message });
    } catch (error) {}
  }
);

export const deleteDiaryEntry = createAsyncThunk(
  "diary/delete",
  async ({ entry_id }: IDeleteDiaryEntry, thunkAPI) => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/diary/${entry_id}`,
        {
          method: "DELETE",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
        }
      );

      if (response.status === 200) {
        const data = await response.json();
        return data;
      }
      const { statusCode, message } = await response.json();
      return thunkAPI.rejectWithValue({ statusCode, message });
    } catch (error) {
      return thunkAPI.rejectWithValue({
        statusCode: 500,
        message: "Internal Server Error.",
      });
    }
  }
);

const diarySlice = createSlice({
  name: "diary",
  initialState,
  reducers: {
    clearDiaryState: (state) => {
      state.entries = [];
      state.isDiaryError = false;
      state.isDiarySuccess = false;
      state.diarySuccess = [];
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getAllMyDiaryEntires.fulfilled, (state, { payload }) => {
      payload.map((p: Diary) => {
        state.entries.push(p);
      });
      state.isDiaryError = false;
      state.isDiaryFetching = false;
      // state.isDiarySuccess = true;
      return state;
    });
    builder.addCase(getAllMyDiaryEntires.pending, (state) => {
      state.isDiaryFetching = true;
      return state;
    });
    builder.addCase(
      getAllMyDiaryEntires.rejected,
      (state, action: AnyAction) => {
        state.isDiaryFetching = false;
        state.isDiaryError = true;
        state.diaryError.push(action?.payload?.message);
      }
    );

    builder.addCase(createDiaryEntry.fulfilled, (state, { payload }) => {
      state.isDiarySuccess = true;
      state.isDiaryFetching = false;
      state.isDiaryError = false;
      state.diarySuccess?.push("Diary entry has been created!");
      return state;
    });
    builder.addCase(createDiaryEntry.pending, (state) => {
      state.isDiaryFetching = true;
      state.isDiarySuccess = false;
      state.isDiaryError = false;
      return state;
    });
    builder.addCase(createDiaryEntry.rejected, (state, action: AnyAction) => {
      state.isDiaryFetching = false;
      state.isDiaryError = true;
      state.diaryError.push(action?.payload?.message);
      return state;
    });
    builder.addCase(updateDiaryEntry.fulfilled, (state, { payload }) => {
      state.isDiarySuccess = true;
      state.isDiaryFetching = false;
      state.isDiaryError = false;
      state.diarySuccess?.push("Diary entry has been updated!");
      return state;
    });
    builder.addCase(updateDiaryEntry.pending, (state) => {
      state.isDiaryFetching = true;
      state.isDiarySuccess = false;
      state.isDiaryError = false;
      return state;
    });
    builder.addCase(updateDiaryEntry.rejected, (state, action: AnyAction) => {
      state.isDiaryFetching = false;
      state.isDiaryError = true;
      state.diaryError.push(action?.payload?.message);
      return state;
    });
    builder.addCase(deleteDiaryEntry.fulfilled, (state, { payload }) => {
      state.isDiarySuccess = true;
      state.isDiaryFetching = false;
      state.isDiaryError = false;
      state.diarySuccess?.push("Diary entry deleted!");

      return state;
    });
    builder.addCase(deleteDiaryEntry.pending, (state) => {
      state.isDiaryFetching = true;
      state.isDiarySuccess = false;
      state.isDiaryError = false;
      return state;
    });
    builder.addCase(deleteDiaryEntry.rejected, (state, action: AnyAction) => {
      state.isDiaryFetching = false;
      state.isDiaryError = true;
      state.diaryError.push(action?.payload?.message);
      return state;
    });
  },
});
export const { clearDiaryState } = diarySlice.actions;
export default diarySlice.reducer;
